import { ArrowRight, BadgeCheck, BookOpen, HandCoins, Mail, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

import { ModuleThumbnail } from "@/components/modules/ModuleThumbnail";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/lib/currency";
import { getModuleDisplayInfo, getOrderedModuleCatalog, MODULE_LEVEL_TONE } from "@/lib/moduleDisplayInfo";


const heroImage = "https://images.pexels.com/photos/35545651/pexels-photo-35545651.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const libraryImage = "https://images.pexels.com/photos/3747523/pexels-photo-3747523.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const studyImage = "https://images.unsplash.com/photo-1539450780284-0f39d744d390?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";


const steps = [
  { title: "Apply online", description: "Submit your details securely and receive a unique student number automatically." },
  { title: "Set your password", description: "Use your one-time setup link to create a secure password for portal access." },
  { title: "Pay the application fee", description: "Complete payment by EFT using your student number as the reference and submit proof of payment for review." },
  { title: "Await approval", description: "The admissions team confirms payment, reviews your application, and sends your approval email." },
];


const highlights = [
  { icon: ShieldCheck, title: "Trustworthy process", description: "Structured approvals, masked identity data in list views, and secure admin audit logging." },
  { icon: BookOpen, title: "Transparent application status", description: "Applicants and students can follow onboarding, payment review, and approval updates with clarity." },
  { icon: HandCoins, title: "Clear payment process", description: "EFT is currently available, with clear instructions for submitting proof of payment." },
];


const faqItems = [
  {
    question: "When will I receive my student number?",
    answer: "Immediately after a successful application submission. It is shown on-screen and used as your EFT reference.",
  },
  {
    question: "When should I log in to the student portal?",
    answer: "Only after you have completed password setup from the secure setup email. Your student number becomes your username after that point.",
  },
  {
    question: "What happens after I pay the application fee?",
    answer: "The super admin confirms payment, reviews the application, and updates your portal status. Once approved, an approval email is sent.",
  },
];


const SectionHeading = ({ eyebrow, title, description, testId }) => (
  <div className="max-w-3xl space-y-3">
    <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid={`${testId}-eyebrow`}>
      {eyebrow}
    </p>
    <h2 className="text-4xl font-semibold leading-[1.08] tracking-[-0.025em] text-slate-950 sm:text-5xl lg:text-[3.1rem]" data-testid={`${testId}-title`}>
      {title}
    </h2>
    <p className="max-w-2xl text-sm leading-7 text-slate-600 sm:text-base sm:leading-8" data-testid={`${testId}-description`}>
      {description}
    </p>
  </div>
);


export const HomePage = ({ publicConfig }) => {
  const modules = getOrderedModuleCatalog(publicConfig?.modules || []);
  const bankingDetails = publicConfig?.banking_details || {};

  return (
    <div className="space-y-24 pb-20">
      <section className="mx-auto grid w-full max-w-7xl gap-10 px-4 pt-10 sm:px-6 sm:pt-14 lg:grid-cols-[1.02fr_0.98fr] lg:gap-14 lg:px-8 lg:pt-24">
        <div className="space-y-8 sm:space-y-10">
          <div className="inline-flex rounded-full border border-[#f3d5a3] bg-white/85 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.26em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="home-hero-badge-text">
            Professional • Academic • Warmly guided
          </div>
          <div className="space-y-5 sm:space-y-7">
            <h1 className="max-w-[41rem] text-4xl font-semibold leading-[1.04] tracking-[-0.03em] text-slate-950 sm:text-5xl lg:text-[3.95rem]" data-testid="home-hero-title-text">
              <span className="block">Begin your Theology</span>
              <span className="block">SLP journey with</span>
              <span className="block">Zallywood and UNISA</span>
            </h1>
            <p className="max-w-xl pt-1 text-base leading-7 text-slate-600 sm:text-[18px] sm:leading-8" data-testid="home-hero-description-text">
              Apply online, receive your unique student number, follow the official EFT guidance, complete secure onboarding, and move into the student portal with clear access and status visibility every step of the way.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button asChild className="min-h-12 rounded-full px-6 py-3 text-sm" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} data-testid="home-hero-apply-button">
              <Link to="/apply">Start your application</Link>
            </Button>
            <Button asChild variant="outline" className="min-h-12 rounded-full bg-transparent px-6 py-3 text-sm" style={{ borderColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} data-testid="home-hero-modules-button">
              <Link to="/modules">Explore the modules</Link>
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-[0.9fr_0.85fr_1.25fr]">
            <Card className="border-stone-200/80 bg-white/85 shadow-none" data-testid="home-stat-application-fee-card">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.24em] text-slate-500" data-testid="home-stat-application-fee-label">Application fee</p>
                <p className="mt-2 text-[2rem] font-semibold leading-none tracking-[-0.02em] text-slate-950" data-testid="home-stat-application-fee-value">R{publicConfig?.application_fee || 150}</p>
              </CardContent>
            </Card>
            <Card className="border-stone-200/80 bg-white/85 shadow-none" data-testid="home-stat-module-count-card">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.24em] text-slate-500" data-testid="home-stat-module-count-label">Total modules</p>
                <p className="mt-2 text-[2rem] font-semibold leading-none tracking-[-0.02em] text-slate-950" data-testid="home-stat-module-count-value">{modules.length || 5}</p>
              </CardContent>
            </Card>
            <Card className="border-stone-200/80 bg-white/85 shadow-none" data-testid="home-stat-support-card">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.24em] text-slate-500" data-testid="home-stat-support-label">Support contact</p>
                <p className="mt-2 text-[0.95rem] font-medium leading-6 text-slate-900" data-testid="home-stat-support-value">{publicConfig?.support_email}</p>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 rounded-[2rem] bg-[#e8dcc9] blur-3xl" />
          <div className="relative overflow-hidden rounded-[2rem] border border-stone-200/80 bg-white shadow-[0_22px_60px_rgba(31,45,61,0.1)]">
            <div className="aspect-[4/4.6] overflow-hidden">
              <img src={heroImage} alt="Students studying in a library" className="h-full w-full object-cover object-center" data-testid="home-hero-image" />
            </div>
            <div className="grid gap-4 border-t border-stone-200/80 bg-[#fcfaf5] p-6 sm:grid-cols-2">
              <div data-testid="home-hero-panel-student-number">
                <p className="text-xs uppercase tracking-[0.24em] text-slate-500">Secure identity flow</p>
                <p className="mt-2 text-sm leading-7 text-slate-700">Each applicant receives a unique student number linked securely to their application record.</p>
              </div>
              <div data-testid="home-hero-panel-payment">
                <p className="text-xs uppercase tracking-[0.24em] text-slate-500">EFT payment process</p>
                <p className="mt-2 text-sm leading-7 text-slate-700">Applicants can complete payment by EFT and submit proof of payment for review.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid w-full max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-stone-200/80 bg-white shadow-[0_24px_70px_rgba(31,45,61,0.08)]">
          <img src={libraryImage} alt="University library shelves" className="aspect-[4/4.2] h-full w-full object-cover" data-testid="home-library-image" />
        </div>
        <div className="space-y-8">
          <SectionHeading
            eyebrow="Why this portal"
            title="Credible, secure, and easy to follow"
            description="The portal provides a secure application process, clear student onboarding, guided EFT instructions, and transparent application status updates."
            testId="home-highlights"
          />

          <div className="grid gap-4 md:grid-cols-3">
            {highlights.map(({ icon: Icon, title, description }) => (
              <Card key={title} className="border-stone-200/80 bg-white/85 shadow-none" data-testid={`home-highlight-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <CardHeader className="space-y-4 pb-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff4e8]" style={{ color: "var(--brand-primary-orange, #F5A623)" }}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="text-[1.5rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid={`home-highlight-title-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-7 text-slate-600" data-testid={`home-highlight-description-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="How it works"
          title="A calm four-step student journey"
          description="Applicants always know what comes next, while the admin team retains the controls needed to confirm payment, manage approvals, and resend critical emails safely."
          testId="home-process"
        />
        <div className="mt-10 grid gap-5 lg:grid-cols-4">
          {steps.map((step, index) => (
            <Card key={step.title} className="border-stone-200/80 bg-white/90 shadow-none" data-testid={`home-process-card-${index + 1}`}>
              <CardContent className="space-y-4 p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1f2d3d] text-lg font-semibold text-white" data-testid={`home-process-number-${index + 1}`}>
                  {index + 1}
                </div>
                <h3 className="text-[1.5rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid={`home-process-title-${index + 1}`}>{step.title}</h3>
                <p className="text-sm leading-7 text-slate-600" data-testid={`home-process-description-${index + 1}`}>{step.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 rounded-[2rem] border border-stone-200/80 bg-white/90 p-5 shadow-[0_24px_70px_rgba(31,45,61,0.08)] sm:p-8 lg:grid-cols-[0.95fr_1.05fr]">
          <SectionHeading
            eyebrow="What happens next"
            title="A clearer, more official admissions path"
            description="The public side explains how application handling, EFT payment, proof submission, and admissions review work in a clear and official way."
            testId="home-admissions"
          />
          <div className="space-y-5">
            <div className="rounded-[1.5rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid="home-admissions-evidence-card">
              <p className="text-sm font-semibold text-slate-900" data-testid="home-admissions-evidence-heading">EFT and proof of payment</p>
              <div className="mt-2 space-y-1.5 text-sm leading-7 text-slate-700">
                <p data-testid="home-admissions-proof-email-text">Proof of payment email: {bankingDetails.proof_of_payment_email}</p>
                <p data-testid="home-admissions-bank-name-text">Bank: {bankingDetails.bank_name}</p>
                <p data-testid="home-admissions-account-name-text">Account Name: {bankingDetails.account_name}</p>
                <p data-testid="home-admissions-account-number-text">Account Number: {bankingDetails.account_number}</p>
                <p data-testid="home-admissions-branch-code-text">Branch Code: {bankingDetails.branch_code}</p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {[
                ["Submit application", "Receive and save your student number immediately."],
                ["Pay by EFT", "Use your student number as the payment reference and send proof of payment."],
                ["Await review", "Admin confirms payment, reviews the application, and updates your status."],
              ].map(([title, description], index) => (
                <Card key={title} className="border-stone-200/80 bg-[#fcfaf5] shadow-none" data-testid={`home-admissions-step-card-${index + 1}`}>
                  <CardContent className="space-y-3 p-5">
                    <p className="text-sm font-semibold text-slate-900" data-testid={`home-admissions-step-title-${index + 1}`}>{title}</p>
                    <p className="text-sm leading-7 text-slate-600" data-testid={`home-admissions-step-description-${index + 1}`}>{description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 rounded-[2rem] border border-stone-200/80 bg-white/90 p-5 shadow-[0_24px_70px_rgba(31,45,61,0.08)] sm:p-8 lg:grid-cols-[0.7fr_1.3fr] lg:gap-10">
          <SectionHeading
            eyebrow="Modules overview"
            title="Explore the five theology modules"
            description="Get a quick view of the learning pathway, module levels, and pricing before exploring the full module details."
            testId="home-modules"
          />
          <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {modules.map((module) => {
                const previewModule = getModuleDisplayInfo(module);

                return (
                <Card key={module.code} className="border-stone-200/80 bg-[#fcfaf5] shadow-none transition-colors hover:border-[#f3d5a3]" data-testid={`home-module-card-${module.code.toLowerCase()}`}>
                  <CardContent className="space-y-4 p-6">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[1.7rem] font-semibold leading-none tracking-[-0.03em] text-slate-950" data-testid={`home-module-code-${module.code.toLowerCase()}`}>{module.code}</p>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${MODULE_LEVEL_TONE[previewModule.level] || "bg-slate-100 text-slate-700"}`} data-testid={`home-module-level-${module.code.toLowerCase()}`}>
                        {previewModule.level}
                      </span>
                    </div>
                    <p className="text-base font-medium leading-7 text-slate-800" data-testid={`home-module-title-${module.code.toLowerCase()}`}>{previewModule.title}</p>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-slate-600" data-testid={`home-module-price-${module.code.toLowerCase()}`}>{formatCurrency(previewModule.price)}</p>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500" data-testid={`home-module-duration-${module.code.toLowerCase()}`}>Duration: {previewModule.duration}</p>
                    </div>
                  </CardContent>
                </Card>
              )})}
            </div>
            <div className="flex flex-col gap-3 border-t border-stone-200/80 pt-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm leading-7 text-slate-600" data-testid="home-modules-helper-text">Want more detail on each module? View the full Modules page.</p>
              <Button asChild className="min-h-12 rounded-full px-6" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} data-testid="home-modules-view-all-button">
                <Link to="/modules">View all modules</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="FAQ"
          title="Quick answers for applicants"
            description="A concise guide for first-time applicants who want to understand exactly how the current application process works before they apply."
          testId="home-faq"
        />
        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {faqItems.map((item, index) => (
            <Card key={item.question} className="border-stone-200/80 bg-white/90 shadow-none" data-testid={`home-faq-card-${index + 1}`}>
              <CardContent className="space-y-4 p-6">
                <h3 className="text-[1.45rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid={`home-faq-question-${index + 1}`}>{item.question}</h3>
                <p className="text-sm leading-7 text-slate-600" data-testid={`home-faq-answer-${index + 1}`}>{item.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="mx-auto grid w-full max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-stone-200/80 bg-white shadow-[0_24px_70px_rgba(31,45,61,0.08)]">
          <img src={studyImage} alt="Student reading a book" className="aspect-[4/3.2] h-full w-full object-cover object-center" data-testid="home-study-image" />
        </div>
        <Card className="border-stone-200/80 text-white shadow-[0_24px_70px_rgba(31,45,61,0.18)]" style={{ backgroundColor: "var(--brand-primary-dark, #2B2B2B)" }} data-testid="home-cta-card">
          <CardContent className="space-y-6 p-8 sm:p-10">
            <BadgeCheck className="h-10 w-10 text-amber-200" />
            <h2 className="text-4xl font-semibold leading-[1.08] tracking-[-0.025em] text-white sm:text-5xl lg:text-[2.5rem]" data-testid="home-cta-title-text">Ready to begin the application process?</h2>
            <p className="text-base leading-8 text-slate-200" data-testid="home-cta-description-text">
              Apply online for the Zallywood x UNISA Theology Short Learning Programme, save your student number immediately, and move into a secure setup flow with clear status guidance.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button asChild className="min-h-12 rounded-full px-6" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} data-testid="home-cta-apply-button">
                <Link to="/apply">Apply now</Link>
              </Button>
              <Button asChild variant="outline" className="min-h-12 rounded-full border-white/30 bg-transparent px-6 text-white hover:bg-white/10" data-testid="home-cta-contact-button">
                <Link to="/contact">Contact support</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};


export const AboutPage = ({ publicConfig }) => (
  <div className="mx-auto max-w-7xl space-y-16 px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
    <SectionHeading
      eyebrow="About the SLP"
      title="A theology programme shaped around structured learning and credible progression"
      description="This portal supports Theology Short Learning Programmes offered through Zallywood and UNISA with trusted application handling, secure identity flows, and careful academic presentation."
      testId="about-hero"
    />

    <div className="grid gap-6 lg:grid-cols-3">
      {[
        ["Secure application process", "Applicants can submit their details confidently through a guided process that protects key personal information."],
        ["Clear student onboarding", "Each applicant receives a student number, password setup guidance, and clear steps for payment and status updates."],
        ["Protected applicant information", "Sensitive identity information is handled with restricted access, careful review controls, and audit logging."],
      ].map(([title, description]) => (
        <Card key={title} className="border-stone-200/80 bg-white/90 shadow-none" data-testid={`about-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <CardContent className="space-y-4 p-6">
            <h3 className="text-[1.45rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid={`about-card-title-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{title}</h3>
            <p className="text-sm leading-7 text-slate-600" data-testid={`about-card-description-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{description}</p>
          </CardContent>
        </Card>
      ))}
    </div>

    <Card className="border-stone-200/80 bg-white/90 shadow-none" data-testid="about-launch-scope-card">
      <CardContent className="grid gap-8 p-5 sm:p-8 lg:grid-cols-2">
        <div className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="about-launch-scope-heading">Current portal services</p>
          <ul className="space-y-3 text-sm leading-7 text-slate-700">
            <li data-testid="about-launch-item-1">Secure online application capture</li>
            <li data-testid="about-launch-item-2">Unique student number creation and password setup by email</li>
            <li data-testid="about-launch-item-3">Student status portal with module information and EFT instructions</li>
            <li data-testid="about-launch-item-4">Admin dashboard for application review, payment confirmation, and communication support</li>
          </ul>
        </div>
        <div className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="about-future-scope-heading">Planned enhancements</p>
          <ul className="space-y-3 text-sm leading-7 text-slate-700">
            <li data-testid="about-future-item-1">Online card payments may be added at a later stage.</li>
            <li data-testid="about-future-item-2">Additional module content and student access tools may be introduced over time.</li>
            <li data-testid="about-future-item-3">Assessment and results services may be added over time.</li>
            <li data-testid="about-future-item-4">Sensitive identity information is handled with restricted access and audit logging.</li>
          </ul>
        </div>
      </CardContent>
    </Card>

    <div className="rounded-[2rem] border border-stone-200/80 p-8 text-white" style={{ backgroundColor: "var(--brand-primary-dark, #2B2B2B)" }} data-testid="about-contact-banner">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="about-contact-heading">Need guidance before applying?</p>
          <p className="mt-2 text-lg text-slate-200" data-testid="about-contact-email">Support: {publicConfig?.support_email}</p>
        </div>
        <Button asChild className="rounded-full px-6" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} data-testid="about-contact-button">
          <Link to="/contact">Visit contact page</Link>
        </Button>
      </div>
    </div>
  </div>
);


export const ModulesPage = ({ publicConfig }) => {
  const publicModules = getOrderedModuleCatalog(publicConfig?.modules || []);

  return (
    <div className="mx-auto max-w-7xl space-y-14 px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
      <SectionHeading
        eyebrow="Modules overview"
        title="Programme structure"
        description="Explore the full theology learning pathway, current module pricing, and the main themes covered in each stage of study."
        testId="modules-hero"
      />

      <div className="space-y-6" data-testid="modules-card-stack">
        {publicModules.map((module) => (
          <Card key={module.code} className="border-stone-200/80 bg-white/90 shadow-none" data-testid={`modules-card-${module.code.toLowerCase()}`}>
            <CardContent className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[15rem_1fr] lg:gap-8 lg:p-7">
              <div className="mx-auto w-full max-w-[16rem] lg:mx-0 lg:max-w-none">
                <ModuleThumbnail module={module} testIdPrefix={`modules-${module.code.toLowerCase()}`} />
              </div>

              <div className="flex min-w-0 flex-col justify-between gap-6">
                <div className="space-y-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid={`modules-code-${module.code.toLowerCase()}`}>
                      {module.code.replace("M", "Module ")}
                    </p>
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${MODULE_LEVEL_TONE[module.level] || "bg-slate-100 text-slate-700"}`} data-testid={`modules-level-${module.code.toLowerCase()}`}>
                      {module.level}
                    </span>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-[1.8rem] font-semibold leading-tight tracking-[-0.025em] text-slate-950" data-testid={`modules-title-${module.code.toLowerCase()}`}>
                      {module.title}
                    </h3>
                    <p className="max-w-3xl text-sm leading-7 text-slate-600 sm:text-base" data-testid={`modules-description-${module.code.toLowerCase()}`}>
                      {module.description}
                    </p>
                  </div>

                  <div className="rounded-[1.35rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid={`modules-explore-${module.code.toLowerCase()}`}>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }}>
                      What you will explore
                    </p>
                    <ul className="mt-3 space-y-2.5 text-sm leading-7 text-slate-700">
                      {module.explore.map((item, index) => (
                        <li key={item} className="flex gap-3" data-testid={`modules-explore-item-${module.code.toLowerCase()}-${index + 1}`}>
                          <span className="mt-2 h-2 w-2 rounded-full" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)" }} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex flex-col gap-4 border-t border-stone-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Current price</p>
                    <p className="mt-1 text-2xl font-semibold tracking-[-0.02em] text-slate-950" data-testid={`modules-price-${module.code.toLowerCase()}`}>{formatCurrency(module.price)}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500" data-testid={`modules-duration-${module.code.toLowerCase()}`}>Duration: {module.duration}</p>
                  </div>
                  <Button asChild className="min-h-12 rounded-full px-6" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} data-testid={`modules-apply-button-${module.code.toLowerCase()}`}>
                    <Link to="/apply">Start application</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-stone-200/80 bg-[#fcfaf5] shadow-none" data-testid="modules-phase-note-card">
        <CardContent className="grid gap-6 p-5 sm:p-8 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="modules-phase-note-heading">What students can do now</p>
            <p className="mt-3 text-sm leading-7 text-slate-700" data-testid="modules-phase-note-body">
              Applicants can review each module’s level, focus areas, and current pricing before they apply. Module access is granted through the student process once payment and approval requirements are complete.
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="modules-coming-soon-heading">Additional services</p>
            <p className="mt-3 text-sm leading-7 text-slate-700" data-testid="modules-coming-soon-body">
              Detailed module materials, guided learning resources, and further student-access tools will be shared through the programme as availability is confirmed.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


export const ContactPage = ({ publicConfig }) => (
  <div className="mx-auto max-w-6xl space-y-12 px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
    <SectionHeading
      eyebrow="Contact Us"
      title="Support for applications, payments, and onboarding"
      description="If you need help with your application, student number, password setup, or EFT payment reference, use the support details below."
      testId="contact-hero"
    />
    <div className="grid gap-6 md:grid-cols-2">
      <Card className="border-stone-200/80 bg-white/90 shadow-none" data-testid="contact-support-card">
        <CardContent className="space-y-5 p-8">
          <Mail className="h-10 w-10" style={{ color: "var(--brand-primary-orange, #F5A623)" }} />
          <h3 className="text-[1.75rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="contact-support-title">Support email</h3>
          <p className="text-base text-slate-700" data-testid="contact-support-email">{publicConfig?.support_email}</p>
        </CardContent>
      </Card>
      <Card className="border-stone-200/80 bg-white/90 shadow-none" data-testid="contact-payment-card">
        <CardContent className="space-y-5 p-8">
          <HandCoins className="h-10 w-10" style={{ color: "var(--brand-primary-orange, #F5A623)" }} />
          <h3 className="text-[1.75rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="contact-payment-title">Payment guidance</h3>
          <p className="text-base leading-7 text-slate-700" data-testid="contact-payment-body">
            Use your student number as the EFT payment reference. After payment, email your proof of payment to {publicConfig?.banking_details?.proof_of_payment_email || "payments@zallywoodunisa.org"}.
          </p>
          <div className="space-y-1 text-sm leading-7 text-slate-600" data-testid="contact-payment-bank-details">
            <p data-testid="contact-payment-bank-name">Bank: {publicConfig?.banking_details?.bank_name || "—"}</p>
            <p data-testid="contact-payment-account-name">Account Name: {publicConfig?.banking_details?.account_name || "—"}</p>
            <p data-testid="contact-payment-account-number">Account Number: {publicConfig?.banking_details?.account_number || "—"}</p>
            <p data-testid="contact-payment-branch-code">Branch Code: {publicConfig?.banking_details?.branch_code || "—"}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);


export const PrivacyPage = ({ publicConfig }) => (
  <div className="mx-auto max-w-5xl space-y-10 px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
    <SectionHeading
      eyebrow="Privacy policy"
      title="How application data is used"
      description="This portal collects personal data only for theology programme application processing, secure onboarding, communication, payment matching, and administrative review."
      testId="privacy-hero"
    />
    <Card className="border-stone-200/80 bg-white/90 shadow-none" data-testid="privacy-content-card">
      <CardContent className="space-y-6 p-8 text-sm leading-8 text-slate-700">
        <p data-testid="privacy-paragraph-1">Applicant profile data acts as the source of truth for personal details. Sensitive identity numbers, addresses, and phone numbers are protected at rest where possible, and list screens avoid exposing full identity values unnecessarily.</p>
        <p data-testid="privacy-paragraph-2">Super admin actions on sensitive data are auditable. Identity values are masked in dashboard list views and can be revealed only when necessary inside the application detail workflow.</p>
        <p data-testid="privacy-paragraph-3">By applying, students consent to the use of their data for application handling, student number creation, transactional communication, and payment administration. Support contact: {publicConfig?.support_email}.</p>
      </CardContent>
    </Card>
  </div>
);


export const TermsPage = ({ publicConfig }) => (
  <div className="mx-auto max-w-5xl space-y-10 px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
    <SectionHeading
      eyebrow="Terms & disclaimer"
      title="Portal usage terms"
      description="These terms set expectations for application submission, payment handling, and student access within the portal."
      testId="terms-hero"
    />
    <Card className="border-stone-200/80 bg-white/90 shadow-none" data-testid="terms-content-card">
      <CardContent className="space-y-6 p-8 text-sm leading-8 text-slate-700">
        <p data-testid="terms-paragraph-1">Submitting an application does not automatically approve admission. Approval depends on payment confirmation and super admin review.</p>
        <p data-testid="terms-paragraph-2">Module content, PDFs, assessments, and certificates are not part of the current access scope. The student portal provides application status, module information, and payment instructions only.</p>
        <p data-testid="terms-paragraph-3">Applicants should use their student number as the EFT payment reference. For support or clarification, contact {publicConfig?.support_email}.</p>
      </CardContent>
    </Card>

    <div className="flex justify-start">
      <Button asChild className="rounded-full px-6" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} data-testid="terms-apply-button">
        <Link to="/apply">Continue to application <ArrowRight className="ml-2 h-4 w-4" /></Link>
      </Button>
    </div>
  </div>
);